import type { MutableState } from '../State';
import {
  AccessoryItemID,
  AccessorySlot,
  AccessoryTierID,
} from './accessory.ruleset';
import {
  getAccessoryItem,
  getAccessoryItemTier,
  getSelectedItemByID,
} from './accessory.getters';

/**
 * Equip accessory item
 */
export function equipAccessoryItem(
  state: MutableState,
  id: AccessoryItemID,
  slot: AccessorySlot,
  tierId: AccessoryTierID,
): void {
  // Get items data
  const item = getAccessoryItem(id);

  // Check if item exist
  if (!item) throw new Error(`Accessory item with ${id} was not found`);

  // Check if slot is correct
  if (item.slot !== slot)
    throw new Error(
      `Accessory item ${id} was tried to be equipped for wrong slot`,
    );

  // Add item to selected list
  state.accessory.selected.push({
    id,
    slot,
    tierId,
  });
}

/**
 * Reset all selected items to default
 */
export function unequipAllAccessoryItems(state: MutableState): void {
  state.accessory.selected = [];
}

/**
 * Unequip accessory item.
 */
function unequipAccessoryItem(state: MutableState, id: AccessoryItemID) {
  state.accessory.selected = state.accessory.selected.filter(
    (item) => item.id !== id,
  );
}

/**
 * Add tier to accessory item. If tier already exist, it will be updated. Won't create new item.
 * @param state
 * @param id
 * @param tier
 * @param now
 * @param quantity
 * @returns
 */
export function addAccessoryItemTier(
  state: MutableState,
  id: AccessoryItemID,
  tier: AccessoryTierID,
  now: number,
  quantity: number = 1,
): void {
  const item = state.accessory.purchased[id];
  if (!item) {
    throw new Error(`Player does not own accessory item ${id}`);
  }

  const tierData = item.tier[tier];
  const tierConfig = getAccessoryItemTier(id, tier);

  // New item.
  if (!tierData) {
    item.tier[tier] = {
      tier: tierConfig.tier,
      createdAt: now,
      updatedAt: now,
      isNew: true,
      quantity,
    };

    return;
  }

  // Update existing item
  tierData.quantity += quantity;
  tierData.updatedAt = now;
}

/**
 * Add accessory item to purchased list. And/or add tier to existing item.
 * @param state
 * @param id
 * @param tier
 * @param now
 */
export function addAccessoryItem(
  state: MutableState,
  id: AccessoryItemID,
  tier: AccessoryTierID,
  now: number,
): void {
  const item = state.accessory.purchased[id];

  if (!item) {
    state.accessory.purchased[id] = {
      purchasedAt: now,
      isNew: true,
      tier: {},
    };
  }

  addAccessoryItemTier(state, id, tier, now);
}

/**
 * Mark all accessory items and tiers as seen.
 * @param state
 */
export function markAccessoryItemsAsSeen(state: MutableState): void {
  Object.values(state.accessory.purchased).forEach((item) => {
    item.isNew = false;
    Object.values(item.tier).forEach((tier) => {
      tier.isNew = false;
    });
  });
}

/**
 * Remove tier from accessory item. If tier quantity is 0, it will be removed.
 */
export function removeAccessoryItemTier(
  state: MutableState,
  id: AccessoryItemID,
  tier: AccessoryTierID,
  quantity: number = 1,
): void {
  const item = state.accessory.purchased[id];
  if (!item) {
    throw new Error(`Player does not own accessory item ${id}`);
  }

  const tierData = item.tier[tier];
  if (!tierData) {
    throw new Error(`Player does not own accessory item ${id} tier ${tier}`);
  }

  tierData.quantity -= quantity;

  // Do not remove tier if quantity is more than 0.
  if (tierData.quantity > 0) return;

  // If item is equipped, unequip it.
  if (getSelectedItemByID(state, id)) {
    unequipAccessoryItem(state, id);
  }

  // Remove tier
  delete item.tier[tier];

  // Remove item if no tiers left.
  if (Object.keys(item.tier).length === 0) {
    delete state.accessory.purchased[id];
  }
}
